<template>
  <action-bar
    v-if="
      isHydrationReady &&
      isPdp &&
      !productStore.decoratedProduct?.isDiscontinued
    "
    ref="actionBarRef"
    :is-expanded="actionBarExpanded"
    :show-checkmark="true"
    :expanded-headline="
      $textDictionary['actionBar.expandedHeadline'] ??
      'actionBar.expandedHeadline'
    "
    :close-button-label="
      $textDictionary['actionBar.closeButtonLabel'] ??
      'actionBar.closeButtonLabel'
    "
    @close="onCloseActionBar"
  >
    <template #mobile-content>
      <product-detail-action-bar-mobile
        :is-initial-loading="isInitialLoading"
        :add-to-cart-button-state="addToCartButtonState"
        :add-to-cart-button-disabled="addToCartButtonDisabled"
        :request-quote-button-state="requestQuoteButtonState"
        :request-quote-button-disabled="requestQuoteButtonDisabled"
        :price-text="productPriceStore.priceText"
        :logged-in="isAuthenticated"
        :display-add-to-cart-button="displayAddToCartButton"
        @add-to-cart="handleAddToCart"
        @show-options="handleShowOptions"
      />
    </template>
    <template #initial-content>
      <product-detail-action-bar-initial
        :delivery-options="deliveryOptions"
        :shipping-tooltip-dictionary-key="shippingTooltipDictionaryKey"
        :is-initial-loading="isInitialLoading"
        :price-text="productPriceStore.priceText"
        :previous-price-text="productStore.decoratedProduct?.previousPriceText"
        :discount-percentage-text="
          productPriceStore?.discountPercentageText ?? ''
        "
        :show-tariff-information="showTariffInformation"
        :pdp-tariff-text="pdpTariffText"
        :amount="amount"
        :add-to-cart-button-state="addToCartButtonState"
        :add-to-cart-button-disabled="addToCartButtonDisabled"
        :request-quote-button-state="requestQuoteButtonState"
        :request-quote-button-disabled="requestQuoteButtonDisabled"
        :logged-in="isAuthenticated"
        :display-add-to-cart-button="displayAddToCartButton"
        :vsc-link="vscLink"
        :vsc-link-text="vscLinkText"
        :max-amount="pdpMaxPurchaseQuantity"
        @update:amount="handleAmountUpdate"
        @add-to-cart="handleAddToCart"
      />
    </template>
    <template #expanded-content>
      <product-detail-action-bar-expanded
        :product-sku="productStore.decoratedProduct?.sku ?? ''"
        :product-name="mainProductDisplayName"
        :price-text="productPriceStore.priceText"
        :cart-items="addedCartItems"
        :cross-sell-cards="
          showProductAddOnsInActionBar ? displayedCrossSellCards : []
        "
        :cross-sell-loading="
          showProductAddOnsInActionBar ? crossSellLoading : false
        "
        :cross-sell-busy="isActionBarBusy"
        :enable-cross-sell="showProductAddOnsInActionBar"
        @go-to-cart="handleGoToCart"
        @go-to-checkout="handleGoToCheckout"
        @cross-sell-add="handleCrossSellAdd"
        @cross-sell-length-change="handleCrossSellLengthChange"
        @cross-sell-more="handleCrossSellMore"
        @length-change="handleStackLengthChange"
      />
    </template>
  </action-bar>
</template>

<script setup lang="ts">
import { ActionBar } from '@hypercodestudio/basler-components';
import type {
  BtnStateType,
  DeliveryOption
} from '@hypercodestudio/basler-components';

import ProductDetailActionBarMobile from './ProductDetailActionBar/ProductDetailActionBarMobile.vue';
import ProductDetailActionBarInitial from './ProductDetailActionBar/ProductDetailActionBarInitial.vue';
import ProductDetailActionBarExpanded from './ProductDetailActionBar/ProductDetailActionBarExpanded.vue';
import { useProductDetailPageStore } from '~/stores/productDetailPageStore/productDetailPageStore';
import { useProductPriceStore } from '~/stores/productDetailPageStore/productPriceStore';
import { useCartStore } from '~/stores/cartStore/cartStore';
import { useProductAddonsStore } from '~/stores/productDetailPageStore/productAddonsStore';
import {
  useCrossSellingProductsStore,
  getGroupId
} from '~/stores/productDetailPageStore/crossSellingProductsStore';
import { isDefined } from '~/utils/guards/isDefined';
import { isProductDetailPage } from '~/utils/shop/isProductDetailPage';

import { useHTMLDictionary } from '~/composables/useHTMLDictionary';
import isSoftwareProduct from '~/utils/shop/isSoftwareProduct';

import { type CartType, CartTypes } from '~/stores/cartStore/model';
import { DecoratedProduct } from '~/utils/shop/DecoratedProduct';
import buildCustomerAreaUrl from '~/utils/shop/buildCustomerAreaUrl';
import { prefetchMagentoProductListAttributeMetadata } from '~/composables/useMagentoProductListAttributeMetadata';
import { useProductFinderAnalytics } from '~/composables/useProductFinderAnalytics';
import { useActionBarCrossSellAnalytics } from '~/composables/useActionBarCrossSellAnalytics';
import { usePdpRecommendedProductsAnalytics } from '~/composables/usePdpRecommendedProductsAnalytics';
import { buildCartableAccessorySelections } from '~/utils/pdpRecommendedProductsAnalytics/buildPdpRecommendedProductsTrackingPayloads';
import { ACTION_BAR_CROSS_SELL_ECOM_SOURCE } from '~/utils/actionBarCrossSellAnalytics/types';
import { getEffectiveMaxPurchaseQuantity } from '~/utils/shop/maxPurchaseQuantityFromStoreConfig';
import {
  buildActionBarCrossSellCards,
  fetchProductsWithParentVariantsBySkus,
  indexProductsBySku,
  mainProductShippingDaysFromDecorated,
  type ActionBarCrossSellCardModel
} from '~/utils/shop/buildActionBarCrossSellCards';
import {
  buildCandidatesForRecommendedSku,
  flattenProductAttrsForDecoratedBuild
} from '~/utils/shop/buildActionBarCrossSellCandidates';
import { applyBvsSimulationCrossSellCards } from '~/utils/shop/applyBvsSimulationCrossSellCards';
import { useBvsSimulationSelectionStore } from '~/stores/productDetailPageStore/bvsSimulationSelectionStore';
import type { ActionBarCrossSellVariant } from '~/utils/shop/selectActionBarCrossSellProduct';
import { sortActionBarCrossSellVariantsByLength } from '~/utils/shop/sortActionBarCrossSellVariantsByLength';
import buildFeatureToggles from '~/utils/buildFeatureToggles';

type CartItemInfo = {
  quantity: number;
  decoratedProduct: DecoratedProduct | any;
  config_id?: string;
  groupId?: string;
  /** Recommended-product SKU for PDP related-products sync (may differ from cart variant). */
  recommendedProductSku?: string;
  lengthVariants?: ActionBarCrossSellVariant[];
  lengthEditOpen?: boolean;
};

/**
 * Map an Action-Bar cart SKU to a value the PDP related-products dropdown understands.
 */
function resolveRelatedProductSku(
  groupId: string,
  sku: string,
  preferredRecommendedSku?: string
): string | null {
  const map = crossSellingProductsStore.skuToGroupIdMap;
  if (map[sku] === groupId) {
    return sku;
  }
  if (preferredRecommendedSku && map[preferredRecommendedSku] === groupId) {
    return preferredRecommendedSku;
  }
  const groupSku = Object.entries(map).find(([, gid]) => gid === groupId)?.[0];
  return groupSku ?? null;
}

function syncRelatedProductSelection(
  groupId: string,
  sku: string,
  preferredRecommendedSku?: string
): void {
  const relatedSku = resolveRelatedProductSku(
    groupId,
    sku,
    preferredRecommendedSku
  );
  if (relatedSku) {
    crossSellingProductsStore.selectCrossSellingProduct(groupId, relatedSku);
  }
}

const logger = useLogger();
const productAddonStore = useProductAddonsStore();
const crossSellingProductsStore = useCrossSellingProductsStore();
const productStore = useProductDetailPageStore();
const cartStore = useCartStore();
const productFinderAnalytics = useProductFinderAnalytics();
const actionBarCrossSellAnalytics = useActionBarCrossSellAnalytics();
const pdpRecommendedProductsAnalytics = usePdpRecommendedProductsAnalytics();
const productPriceStore = useProductPriceStore();
const bvsSimulationSelectionStore = useBvsSimulationSelectionStore();
const { isAuthenticated } = useShopAuth();
const pdpConfiguration = usePdpConfiguration();

const isPdp = computed(() => isProductDetailPage());

const {
  $textDictionary,
  $shopStore,
  $activeLocale,
  $storeConfig,
  $dictionary,
  $globalPageSettings
} = useNuxtApp();

const actionBarRef = ref<InstanceType<typeof ActionBar> | null>(null);
// The layout starts rendering before the async PDP data has populated the
// store on the server, while the client receives that store in its payload.
// Mounting the interactive action bar after hydration keeps both initial
// trees identical without changing when its stores are initialized.
const isHydrationReady = ref(false);
onMounted(() => {
  isHydrationReady.value = true;
});

// The bar mounts only after hydration, so its template never reads
// `maintenanceMode.text` during SSR. Touch the entry here so the hydration
// snapshot already contains it when the maintenance alert renders on the client.
if ($globalPageSettings.value?.shopConfig?.maintenanceMode) {
  void $dictionary.value['maintenanceMode.text'];
}
const isInitialLoading = computed(
  () => productStore.loading || !productStore.decoratedProduct
);
const addToCartButtonState = ref<BtnStateType>('initial');
const addToCartButtonDisabled = ref(false);
const requestQuoteButtonState = ref<BtnStateType>('initial');
const requestQuoteButtonDisabled = ref(false);

const amount = ref(1);
const actionBarExpanded = ref(false);

const pdpMaxPurchaseQuantity = computed(() =>
  getEffectiveMaxPurchaseQuantity(
    $storeConfig.value,
    productStore.decoratedProduct?.product?.attribute_set_id ?? null
  )
);

watch(
  [pdpMaxPurchaseQuantity, () => productStore.decoratedProduct?.sku],
  () => {
    if (amount.value > pdpMaxPurchaseQuantity.value) {
      amount.value = pdpMaxPurchaseQuantity.value;
    }
  }
);
const addedCartItems = ref<CartItemInfo[]>([]);
const crossSellCards = ref<ActionBarCrossSellCardModel[]>([]);
const crossSellLoading = ref(false);
const isActionBarBusy = ref(false);
const htmlDictionary = useHTMLDictionary(['pdp.tariff.text']);

const showProductAddOnsInActionBar = computed(() => {
  const featureToggles = buildFeatureToggles($globalPageSettings.value);
  const enabledLocales = featureToggles?.showProductAddOnsInActionBar ?? [];
  return enabledLocales.includes($activeLocale.value.webapp);
});

const cartStackSkus = computed(() =>
  addedCartItems.value
    .map((item) => item.decoratedProduct?.sku as string | undefined)
    .filter(isDefined)
);

/**
 * The recommendations plus the products the BVS simulation currently uses.
 * Only populated while the simulation is on the page.
 */
const displayedCrossSellCards = computed(() =>
  applyBvsSimulationCrossSellCards({
    cards: crossSellCards.value,
    lens: bvsSimulationSelectionStore.lens,
    light: bvsSimulationSelectionStore.light,
    stackSkus: cartStackSkus.value
  })
);

const mainProductDisplayName = computed(() => {
  const product = productStore.decoratedProduct;
  if (!product) {
    return '';
  }
  return [product.family, product.modelName].filter(Boolean).join(' ');
});

/**
 * Quantity the main product was added with. Cross-sell adds mirror it, e.g.
 * 2 cameras get 2 tripods — same behaviour as the PDP recommended-products dropdowns.
 */
const mainProductAddedQuantity = computed(() => {
  const mainSku = productStore.decoratedProduct?.sku;
  const mainItem = addedCartItems.value.find(
    (item) => item.decoratedProduct?.sku === mainSku
  );
  const quantity = mainItem?.quantity ?? amount.value;
  return typeof quantity === 'number' && quantity > 0 ? quantity : 1;
});

const actionBarCartType = computed(() => {
  return cartStore.activeCartType === CartTypes.CUSTOMER_CART
    ? CartTypes.CUSTOMER_CART
    : CartTypes.CART;
});

const pdpTariffText = htmlDictionary.value['pdp.tariff.text'] ?? '';

const shippingTooltipDictionaryKey = computed(() => {
  if (
    !productStore.decoratedProduct?.showShippingTime ||
    isSoftware.value ||
    !$dictionary.value['pdp.shippingOption.standard.tooltip.text']
  ) {
    return undefined;
  }

  return 'pdp.shippingOption.standard.tooltip.text';
});

const selectedAccessories = ref(
  getVSCAccessories(crossSellingProductsStore.selectedCrossSellingSkus)
);

const isSoftware = computed(() =>
  isSoftwareProduct(productStore.decoratedProduct)
);

const isVirtualService = computed(() => {
  return (
    productStore.decoratedProduct?.product?.attribute_set_name ===
    'Pim Virtual Services (virtual_services)'
  );
});

const deliveryOptions = computed((): DeliveryOption[] => {
  const options: DeliveryOption[] = [];

  if (!productStore.decoratedProduct?.showShippingTime) {
    options.push({
      iconName: isSoftware.value ? 'software' : 'available',
      title: isSoftware.value
        ? ($textDictionary.value[
            'pdp.shippingOptionWithoutShippingTime.software.title'
          ] ?? 'pdp.shippingOptionWithoutShippingTime.software.title')
        : ($textDictionary.value['pdp.shippingOption.standard.title'] ??
          'pdp.shippingOption.standard.title'),
      titleAddition: productStore.decoratedProduct?.shippingTimeText ?? ''
    });
  } else {
    options.push({
      iconName: isSoftware.value ? 'software' : 'available',
      title: isSoftware.value
        ? ($textDictionary.value['pdp.shippingOption.software.title'] ??
          'pdp.shippingOption.software.title')
        : ($textDictionary.value['pdp.shippingOption.standard.title'] ?? ''),
      titleAddition: productStore.decoratedProduct?.shippingTimeText ?? '',
      text: $textDictionary.value['pdp.shippingOption.standard.text'] ?? ''
    });
  }

  if (
    productStore.decoratedProduct?.showShippingTime &&
    $dictionary.value['pdp.shippingOption.standard.tooltip.text'] &&
    !isSoftware.value
  ) {
    options[0].tooltip = {
      tag: 'button'
    };
  }

  if (productStore.decoratedProduct?.hasFastDelivery) {
    options.push({
      iconName: 'truck-fast',
      title:
        $textDictionary.value['pdp.shippingOption.fastDelivery.title'] ?? '',
      titleAddition:
        $textDictionary.value['pdp.shippingOption.fastDelivery.time'] ?? '',
      text: $textDictionary.value['pdp.shippingOption.fastDelivery.text'] ?? ''
    });
    if ($dictionary.value['pdp.shippingOption.fastDelivery.tooltip.text']) {
      options[1].tooltip = {
        tag: 'button'
      };
    }
  }

  return options;
});

const showTariffInformation = computed(() => {
  return (
    $shopStore.value === 'amer_en' &&
    !isSoftware.value &&
    !isVirtualService.value
  );
});

const displayAddToCartButton = computed(
  () =>
    !isInitialLoading.value &&
    (productStore.decoratedProduct?.isBuyable() ?? false)
);

const vscLinkText = computed(() =>
  productStore.decoratedProduct?.showVSC
    ? ($textDictionary.value['pdp.cart.vsc.label'] ?? '')
    : ''
);

const vscLink = computed(() => {
  const link = productStore.decoratedProduct?.getVSCLink(
    pdpConfiguration.value,
    productStore.vscPageLink,
    selectedAccessories.value
  );
  return typeof link?.uri === 'string' ? link.uri : undefined;
});

function getVSCAccessories(selectedCrossSellingSkus: readonly string[]) {
  if (selectedCrossSellingSkus.length) {
    return `;accessories=[${selectedCrossSellingSkus.toString()}]`;
  }
  return;
}

function handleAmountUpdate(value: number) {
  amount.value = value;
}

function onCloseActionBar() {
  addToCartButtonState.value = 'initial';
  requestQuoteButtonState.value = 'initial';

  // Persist Action-Bar add-ons into PDP related-products selection
  if (showProductAddOnsInActionBar.value) {
    for (const item of addedCartItems.value) {
      const sku = item.decoratedProduct?.sku as string | undefined;
      const groupId =
        item.groupId ??
        (sku ? crossSellingProductsStore.skuToGroupIdMap[sku] : undefined) ??
        (item.recommendedProductSku
          ? crossSellingProductsStore.skuToGroupIdMap[
              item.recommendedProductSku
            ]
          : undefined);
      if (sku && groupId && sku !== productStore.decoratedProduct?.sku) {
        syncRelatedProductSelection(groupId, sku, item.recommendedProductSku);
      }
    }
  }

  addedCartItems.value = [];
  actionBarExpanded.value = false;
  actionBarCrossSellAnalytics.analyticsStore.resetSession();
}

/**
 * Helper function to get DecoratedProduct from addon store by option UID
 */
function getAddonDecoratedProduct(optionUid: string): DecoratedProduct | null {
  const option = productAddonStore.optionsMap[optionUid];
  if (!option?.product) {
    return null;
  }

  return DecoratedProduct.build(
    option.product as any,
    $textDictionary.value,
    $activeLocale.value.webapp
  );
}

/**
 * Helper function to get DecoratedProduct from cross-selling store by SKU
 */
async function getCrossSellingDecoratedProduct(
  sku: string
): Promise<DecoratedProduct | null> {
  // Try to get from store first
  try {
    const stateObj = crossSellingProductsStore._state;
    if (stateObj && stateObj.crossSellingProductsMap) {
      const productsMap = stateObj.crossSellingProductsMap.value as Record<
        string,
        any
      >;
      if (productsMap && productsMap[sku]) {
        return DecoratedProduct.build(
          productsMap[sku] as any,
          $textDictionary.value,
          $activeLocale.value.webapp
        );
      }
    }
  } catch (error) {
    logger.warn('Error accessing cross-selling products from store', error);
  }

  // Fallback: Load product from API
  try {
    const response = await $fetch<any>('/api/magento/products', {
      retry: 0,
      query: {
        store: $shopStore.value,
        locale: $activeLocale.value.webapp,
        filters: JSON.stringify({}),
        skus: sku
      }
    });

    if (response?.items && response.items.length > 0) {
      prefetchMagentoProductListAttributeMetadata(
        $shopStore.value,
        $activeLocale.value.webapp,
        response.items.map((item: any) => item?.sku)
      );
      return DecoratedProduct.build(
        response.items[0] as any,
        $textDictionary.value,
        $activeLocale.value.webapp
      );
    }
  } catch (error) {
    logger.error(`Failed to load product with SKU ${sku}`, error);
  }

  return null;
}

async function handleAddToCart(type: CartType) {
  if (!productStore.decoratedProduct) {
    logger.warn('no active product given - aborting addToCart');
    return;
  }
  addToCartButtonState.value = 'initial';
  requestQuoteButtonState.value = 'initial';

  addToCartButtonState.value =
    type === CartTypes.CART || type === CartTypes.CUSTOMER_CART
      ? 'loading'
      : 'initial';
  requestQuoteButtonState.value =
    type === CartTypes.REQUEST_CART ? 'loading' : 'initial';
  addToCartButtonDisabled.value = true;
  requestQuoteButtonDisabled.value = true;

  const sku = productStore.decoratedProduct.sku;
  // workaround for proper typing
  const passedQuantity: unknown = amount.value;

  if (
    !sku ||
    // empty user input
    (typeof passedQuantity === 'string' && passedQuantity.trim() === '') ||
    (typeof passedQuantity === 'number' && passedQuantity <= 0)
  ) {
    return;
  }

  // Main product
  const cartItemsForDisplay: CartItemInfo[] = [
    {
      decoratedProduct: productStore.decoratedProduct,
      quantity: amount.value
    }
  ];

  // Prepare cart items with DecoratedProducts for display
  // Add additional addon-products if given
  // @see https://gcp.baslerweb.com/jira/browse/DBP-448
  for (const optionUid of Object.values(productAddonStore.selectedOptions)) {
    const decoratedProduct = getAddonDecoratedProduct(optionUid);
    if (decoratedProduct) {
      cartItemsForDisplay.push({
        decoratedProduct,
        quantity: 1
      });
    }
  }

  // Add additional cross-selling products if given
  // @see https://gcp.baslerweb.com/jira/browse/DBP-1176
  for (const crossSellingSku of crossSellingProductsStore.selectedCrossSellingSkus) {
    const decoratedProduct =
      await getCrossSellingDecoratedProduct(crossSellingSku);
    if (decoratedProduct) {
      cartItemsForDisplay.push({
        decoratedProduct,
        quantity: amount.value // same quantity as the product itself! e.g. 2 cameras with 2 cables (via cross selling, for each cam one cable)
      });
    }
  }

  // Add accessory if selected
  if (crossSellingProductsStore?.productFinderSelectedProduct) {
    const decoratedAccessory = DecoratedProduct.build(
      crossSellingProductsStore.productFinderSelectedProduct as any,
      $textDictionary.value,
      $activeLocale.value.webapp
    );
    cartItemsForDisplay.push({
      decoratedProduct: decoratedAccessory,
      quantity: 1
    });
  }

  // For the actual cart API call, we still use SKUs for addon and cross-selling products
  const cartItems = [
    {
      decoratedProduct: productStore.decoratedProduct,
      quantity: amount.value,
      breadcrumbs: productStore.breadcrumbs
    },
    // add additional addon-products if given
    ...productAddonStore.selectedOptionSkus.map((sku) => ({
      quantity: 1,
      sku
    })),
    // add additional cross-selling products if given
    ...crossSellingProductsStore.selectedCrossSellingSkus.map((sku) => ({
      quantity: amount.value,
      sku
    }))
  ];

  const accessory = crossSellingProductsStore?.productFinderSelectedProduct?.sku
    ? {
        quantity: 1,
        sku: crossSellingProductsStore?.productFinderSelectedProduct?.sku ?? ''
      }
    : undefined;

  if (accessory) {
    productFinderAnalytics.registerAccessoryAttribution(
      accessory.sku,
      productStore.decoratedProduct?.sku
    );
    cartItems.push(accessory);
  }

  const mainSku = productStore.decoratedProduct.sku;
  if (
    mainSku &&
    crossSellingProductsStore.selectedCrossSellingSkus.length > 0
  ) {
    const selections = buildCartableAccessorySelections({
      items: crossSellingProductsStore.items,
      selectedByGroupId: crossSellingProductsStore.selectedCrossSellingProducts
    });

    pdpRecommendedProductsAnalytics.registerSelectedAccessories({
      mainProductSku: mainSku,
      selections
    });
  }

  await cartStore.addProducts(
    cartItems,
    type,
    accessory ? { source: 'productFinder' } : undefined
  );
  addToCartButtonDisabled.value = false;
  requestQuoteButtonDisabled.value = false;
  addToCartButtonState.value =
    (type === CartTypes.CART || type === CartTypes.CUSTOMER_CART) &&
    cartStore.lastAction?.type === 'addItems' &&
    cartStore.lastAction?.success &&
    !isDefined(cartStore.lastAction?.error)
      ? 'success'
      : (type === CartTypes.CART || type === CartTypes.CUSTOMER_CART) &&
          cartStore.lastAction?.type === 'addItems' &&
          isDefined(cartStore.lastAction?.error)
        ? 'error'
        : 'initial';
  requestQuoteButtonState.value =
    type === CartTypes.REQUEST_CART &&
    cartStore.lastAction?.type === 'addItems' &&
    cartStore.lastAction?.success &&
    !isDefined(cartStore.lastAction?.error)
      ? 'success'
      : type === CartTypes.REQUEST_CART &&
          cartStore.lastAction?.type === 'addItems' &&
          isDefined(cartStore.lastAction?.error)
        ? 'error'
        : 'initial';

  if (
    cartStore.lastAction?.type === 'addItems' &&
    cartStore.lastAction?.success &&
    !isDefined(cartStore.lastAction?.error) &&
    (type === CartTypes.CART || type === CartTypes.CUSTOMER_CART)
  ) {
    actionBarExpanded.value = true;
    addedCartItems.value = cartItemsForDisplay.map((item) => {
      const sku = item.decoratedProduct?.sku as string | undefined;
      const groupId = sku
        ? crossSellingProductsStore.skuToGroupIdMap[sku]
        : undefined;
      return {
        ...item,
        groupId,
        recommendedProductSku: sku
      };
    });
  }
}

async function ensureRecommendedProductsLoaded(): Promise<void> {
  if (crossSellingProductsStore.recommendedProducts.length > 0) {
    return;
  }
  const sku = productStore.decoratedProduct?.sku;
  if (!sku) {
    return;
  }
  await productStore.loadRecommendedProducts(sku);
}

async function loadCrossSellRecommendations(): Promise<void> {
  if (!showProductAddOnsInActionBar.value) {
    crossSellCards.value = [];
    return;
  }

  const mainSku = productStore.decoratedProduct?.sku;
  if (!mainSku) {
    crossSellCards.value = [];
    return;
  }

  actionBarCrossSellAnalytics.startSession({
    cartId: cartStore.activeCart?.id,
    mainProductSku: mainSku,
    attributeSetName: productStore.decoratedProduct?.getAttributeSetName?.()
  });

  crossSellLoading.value = true;
  crossSellCards.value = [];

  try {
    await ensureRecommendedProductsLoaded();
    await rebuildCrossSellCards();
    trackCrossSellImpression();
  } catch (error) {
    logger.error('Failed to load action bar cross-sell recommendations', error);
    crossSellCards.value = [];
  } finally {
    crossSellLoading.value = false;
    nextTick(syncActionBarHeightCssVariable);
  }
}

function trackCrossSellImpression(): void {
  const groups = crossSellingProductsStore.recommendedProducts;
  if (crossSellCards.value.length > 0) {
    actionBarCrossSellAnalytics.trackShown({
      cards: crossSellCards.value,
      browsedGroupIds: crossSellingProductsStore.browsedGroupIds
    });
    return;
  }

  const stackSkus = addedCartItems.value
    .map((item) => item.decoratedProduct?.sku as string | undefined)
    .filter(isDefined);
  const openGroupCount = groups.filter((group) => {
    if (!group) {
      return false;
    }
    const groupId = getGroupId(group);
    return !stackSkus.some(
      (sku) => crossSellingProductsStore.skuToGroupIdMap[sku] === groupId
    );
  }).length;

  actionBarCrossSellAnalytics.trackHidden(
    actionBarCrossSellAnalytics.resolveHiddenReason({
      hasRecommendedProducts: groups.length > 0,
      openGroupCount,
      cardsAfterDeliveryFilter: crossSellCards.value.length
    })
  );
}

async function rebuildCrossSellCards(): Promise<void> {
  const groups = crossSellingProductsStore.recommendedProducts;
  if (!groups.length) {
    crossSellCards.value = [];
    return;
  }

  const stackSkus = addedCartItems.value
    .map((item) => item.decoratedProduct?.sku as string | undefined)
    .filter(isDefined);

  const openGroups = groups.filter((group) => {
    if (!group) {
      return false;
    }
    const groupId = getGroupId(group);
    return !stackSkus.some(
      (sku) => crossSellingProductsStore.skuToGroupIdMap[sku] === groupId
    );
  });

  const skusToFetch = [
    ...openGroups.flatMap(
      (group) => group?.products?.map((product) => product?.sku) ?? []
    ),
    ...stackSkus
  ].filter(isDefined);

  const uniqueSkus = [...new Set(skusToFetch)];
  const items = await fetchProductsWithParentVariantsBySkus(
    uniqueSkus,
    $shopStore.value,
    $activeLocale.value.webapp
  );
  const productsBySku = indexProductsBySku(items);

  addedCartItems.value = addedCartItems.value.map((item) => {
    const sku = item.decoratedProduct?.sku as string | undefined;
    if (!sku) {
      return item;
    }

    const productPayload = productsBySku.get(sku);
    let decoratedProduct = item.decoratedProduct;
    if (productPayload) {
      try {
        decoratedProduct = DecoratedProduct.build(
          flattenProductAttrsForDecoratedBuild(productPayload) as any,
          $textDictionary.value,
          $activeLocale.value.webapp
        );
      } catch {
        // keep existing decorated product
      }
    }

    const candidate = buildCandidatesForRecommendedSku(sku, productsBySku);
    if (!candidate || candidate.variants.length <= 1) {
      return {
        ...item,
        decoratedProduct
      };
    }
    return {
      ...item,
      decoratedProduct,
      lengthVariants: sortActionBarCrossSellVariantsByLength(
        item.lengthVariants ?? candidate.variants
      ),
      lengthEditOpen: false
    };
  });

  crossSellCards.value = buildActionBarCrossSellCards({
    groups,
    stackSkus,
    skuToGroupIdMap: crossSellingProductsStore.skuToGroupIdMap,
    productsBySku,
    mainProductShippingDays: mainProductShippingDaysFromDecorated(
      productStore.decoratedProduct
    ),
    browsedGroupIds: crossSellingProductsStore.browsedGroupIds,
    locale: $activeLocale.value.webapp
  });
}

async function buildDecoratedProductForStack(
  sku: string
): Promise<DecoratedProduct | null> {
  try {
    const items = await fetchProductsWithParentVariantsBySkus(
      [sku],
      $shopStore.value,
      $activeLocale.value.webapp
    );
    if (items[0]) {
      return DecoratedProduct.build(
        flattenProductAttrsForDecoratedBuild(items[0]) as any,
        $textDictionary.value,
        $activeLocale.value.webapp
      );
    }
  } catch (error) {
    logger.warn(
      'Failed to build stack product from parent-variants API, falling back',
      error
    );
  }

  return getCrossSellingDecoratedProduct(sku);
}

async function handleCrossSellAdd(sku: string, groupId: string): Promise<void> {
  if (isActionBarBusy.value || !showProductAddOnsInActionBar.value) {
    return;
  }

  isActionBarBusy.value = true;
  const cardIndex = displayedCrossSellCards.value.findIndex(
    (entry) => entry.groupId === groupId
  );
  const card =
    cardIndex >= 0 ? displayedCrossSellCards.value[cardIndex] : undefined;
  const cartType = actionBarCartType.value;
  const position = cardIndex >= 0 ? cardIndex + 1 : 1;
  const hasLengthVariants = (card?.variants.length ?? 0) > 1;
  const selectedVariant = card?.variants.find((variant) => variant.sku === sku);
  const quantity = mainProductAddedQuantity.value;

  if (card) {
    actionBarCrossSellAnalytics.trackAddClick({
      groupLabel: card.groupLabel || card.groupId,
      productSku: card.productSku,
      selectedLengthSku: hasLengthVariants ? sku : null,
      position,
      price: selectedVariant?.priceValue ?? undefined
    });
    actionBarCrossSellAnalytics.registerAddAttribution({
      sku,
      groupLabel: card.groupLabel || card.groupId,
      position
    });
  }

  try {
    await cartStore.addProducts([{ sku, quantity }], cartType, {
      source: ACTION_BAR_CROSS_SELL_ECOM_SOURCE
    });

    if (
      cartStore.lastAction?.type !== 'addItems' ||
      !cartStore.lastAction?.success ||
      isDefined(cartStore.lastAction?.error)
    ) {
      return;
    }

    const decoratedProduct = await buildDecoratedProductForStack(sku);
    if (decoratedProduct) {
      addedCartItems.value = [
        ...addedCartItems.value,
        {
          decoratedProduct,
          quantity,
          groupId,
          recommendedProductSku: card?.productSku,
          lengthVariants:
            card && card.variants.length > 1
              ? sortActionBarCrossSellVariantsByLength(card.variants)
              : undefined,
          lengthEditOpen: false
        }
      ];
    }

    // Mirror selection into PDP related-products dropdowns
    syncRelatedProductSelection(groupId, sku, card?.productSku);

    crossSellCards.value = crossSellCards.value.filter(
      (entry) => entry.groupId !== groupId
    );
  } catch (error) {
    logger.error('Failed to add cross-sell product from action bar', error);
  } finally {
    isActionBarBusy.value = false;
    nextTick(syncActionBarHeightCssVariable);
  }
}

function handleCrossSellLengthChange(
  sku: string,
  groupId: string,
  position: number
): void {
  const card = displayedCrossSellCards.value.find(
    (entry) => entry.groupId === groupId
  );
  if (!card) {
    return;
  }

  actionBarCrossSellAnalytics.trackLengthChanged({
    groupLabel: card.groupLabel || card.groupId,
    productSku: card.productSku,
    selectedLengthSku: sku,
    position
  });

  // Keep card model in sync for subsequent shown/add payloads
  const nextCards = crossSellCards.value.map((entry) => {
    if (entry.groupId !== groupId) {
      return entry;
    }
    return {
      ...entry,
      selectedVariantSku: sku
    };
  });
  crossSellCards.value = nextCards;
}

function handleCrossSellMore(remainingCount: number): void {
  actionBarCrossSellAnalytics.trackMoreProductsClick(remainingCount);
}

async function handleStackLengthChange(
  sku: string,
  newVariantSku: string
): Promise<void> {
  if (
    isActionBarBusy.value ||
    sku === newVariantSku ||
    !showProductAddOnsInActionBar.value
  ) {
    return;
  }

  const itemIndex = addedCartItems.value.findIndex(
    (item) => item.decoratedProduct?.sku === sku
  );
  if (itemIndex < 0) {
    return;
  }

  const item = addedCartItems.value[itemIndex];
  const quantity = item.quantity;
  const cartType = actionBarCartType.value;
  const cartItem = cartStore.activeCartItems.find(
    (entry) => entry.decoratedProduct?.sku === sku
  );

  if (!cartItem?.uid) {
    logger.warn('Cannot change cable length: cart item uid missing', { sku });
    return;
  }

  isActionBarBusy.value = true;
  const previousItems = [...addedCartItems.value];

  try {
    await cartStore.removeFromCart(cartItem.uid, cartType, { silent: true });
    await cartStore.addProducts([{ sku: newVariantSku, quantity }], cartType);

    if (
      cartStore.lastAction?.type !== 'addItems' ||
      !cartStore.lastAction?.success ||
      isDefined(cartStore.lastAction?.error)
    ) {
      addedCartItems.value = previousItems;
      return;
    }

    const decoratedProduct = await buildDecoratedProductForStack(newVariantSku);
    if (!decoratedProduct) {
      addedCartItems.value = previousItems;
      return;
    }

    const next = [...addedCartItems.value];
    next[itemIndex] = {
      ...item,
      decoratedProduct,
      lengthEditOpen: false
    };
    addedCartItems.value = next;

    if (item.groupId) {
      syncRelatedProductSelection(
        item.groupId,
        newVariantSku,
        item.recommendedProductSku
      );
    }
  } catch (error) {
    logger.error('Failed to change action bar cable length', error);
    addedCartItems.value = previousItems;
  } finally {
    isActionBarBusy.value = false;
  }
}

function handleShowOptions() {
  // Toggle the mobile view to show buying options
  actionBarRef.value?.toggleMobile();
}

function handleGoToCart() {
  if (cartStore.currentCartLink) {
    enhancedNavigateTo({ path: cartStore.currentCartLink });
  }
}

function handleGoToCheckout() {
  const checkoutUrl = buildCustomerAreaUrl('cartCheckoutPage');

  if (checkoutUrl) {
    enhancedNavigateTo({ path: checkoutUrl });
  }
}

watch(
  () => crossSellingProductsStore.selectedCrossSellingSkus,
  () => {
    if (crossSellingProductsStore.selectedCrossSellingSkus) {
      selectedAccessories.value = getVSCAccessories(
        crossSellingProductsStore.selectedCrossSellingSkus
      );
    }
  }
);

function syncActionBarHeightCssVariable(): void {
  const actionBarElement = actionBarRef.value?.$el;

  if (!(actionBarElement instanceof HTMLElement)) {
    return;
  }

  document.documentElement.style.setProperty(
    '--action-bar-height',
    `${actionBarElement.offsetHeight}px`
  );
}

onMounted(() => {
  nextTick(syncActionBarHeightCssVariable);
});

watch(actionBarExpanded, (expanded) => {
  nextTick(syncActionBarHeightCssVariable);
  if (expanded && showProductAddOnsInActionBar.value) {
    void loadCrossSellRecommendations();
  } else {
    crossSellCards.value = [];
    if (!expanded) {
      actionBarCrossSellAnalytics.analyticsStore.resetSession();
    }
  }
});

watch(
  () => displayedCrossSellCards.value.length,
  () => {
    nextTick(syncActionBarHeightCssVariable);
  }
);

onUnmounted(() => {
  document.documentElement.style.removeProperty('--action-bar-height');
});
</script>
